import { Link } from "react-router-dom";
import { Compass, WhatsappLogo, InstagramLogo, EnvelopeSimple } from "@phosphor-icons/react";
import {
  WHATSAPP_URL, INSTAGRAM_URL, MAILTO_URL, INSTAGRAM_HANDLE, SUPPORT_EMAIL,
  trackContactClick,
} from "@/lib/contact";

/**
 * Site-wide footer used by Landing and Contact pages. Pure dark glass aesthetic
 * matching the rest of Career GPS AI. Includes a compact contact block so
 * support is always one click away.
 */
export default function Footer({ source = "footer" }) {
  return (
    <footer className="border-t border-white/5 bg-[#050505] px-4 sm:px-6 md:px-12 lg:px-24 pt-12 sm:pt-16 pb-8" data-testid="site-footer">
      <div className="max-w-[1400px] mx-auto grid gap-10 md:gap-12 md:grid-cols-12">
        {/* Brand */}
        <div className="md:col-span-5">
          <Link to="/" className="inline-flex items-center gap-2" data-testid="footer-logo">
            <Compass size={26} weight="duotone" className="text-blue-500" />
            <span className="font-medium tracking-tight text-lg">
              Career GPS <span className="text-blue-500">AI</span>
            </span>
          </Link>
          <p className="mt-4 text-sm text-zinc-500 max-w-xs leading-relaxed">
            The AI-powered career operating system. Roadmaps, coaching and interview prep
            built for ambitious people in every corner of the world.
          </p>
        </div>

        {/* Product links */}
        <div className="md:col-span-3">
          <div className="text-xs tracking-[0.2em] uppercase text-zinc-500 font-semibold mb-4">Product</div>
          <ul className="space-y-2.5 text-sm">
            <li><a href="/#features" className="text-zinc-300 hover:text-white" data-testid="footer-link-features">Features</a></li>
            <li><a href="/#pricing" className="text-zinc-300 hover:text-white" data-testid="footer-link-pricing">Pricing</a></li>
            <li><Link to="/signup" className="text-zinc-300 hover:text-white" data-testid="footer-link-signup">Get started</Link></li>
            <li><Link to="/login" className="text-zinc-300 hover:text-white" data-testid="footer-link-login">Sign in</Link></li>
          </ul>
        </div>

        {/* Contact mini-block */}
        <div className="md:col-span-4">
          <div className="text-xs tracking-[0.2em] uppercase text-zinc-500 font-semibold mb-4">Contact us</div>
          <p className="text-sm text-zinc-400 mb-4 leading-relaxed">
            Need help with your career journey? We&apos;re one tap away.
          </p>
          <div className="flex flex-col gap-2.5">
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackContactClick("whatsapp", source)}
              data-testid="footer-contact-whatsapp"
              className="group inline-flex items-center gap-3 text-sm text-zinc-300 hover:text-white"
            >
              <span className="w-8 h-8 rounded-md bg-emerald-500/10 border border-emerald-500/20 group-hover:border-emerald-500/40 flex items-center justify-center transition-colors">
                <WhatsappLogo size={16} weight="fill" className="text-emerald-400" />
              </span>
              WhatsApp chat
            </a>
            <a
              href={INSTAGRAM_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackContactClick("instagram", source)}
              data-testid="footer-contact-instagram"
              className="group inline-flex items-center gap-3 text-sm text-zinc-300 hover:text-white"
            >
              <span className="w-8 h-8 rounded-md bg-pink-500/10 border border-pink-500/20 group-hover:border-pink-500/40 flex items-center justify-center transition-colors">
                <InstagramLogo size={16} weight="fill" className="text-pink-400" />
              </span>
              @{INSTAGRAM_HANDLE}
            </a>
            <a
              href={MAILTO_URL}
              onClick={() => trackContactClick("email", source)}
              data-testid="footer-contact-email"
              className="group inline-flex items-center gap-3 text-sm text-zinc-300 hover:text-white break-all"
            >
              <span className="w-8 h-8 rounded-md bg-blue-500/10 border border-blue-500/20 group-hover:border-blue-500/40 flex items-center justify-center transition-colors shrink-0">
                <EnvelopeSimple size={16} weight="fill" className="text-blue-400" />
              </span>
              {SUPPORT_EMAIL}
            </a>
          </div>
          <Link
            to="/contact"
            data-testid="footer-contact-cta"
            className="mt-5 inline-flex items-center gap-2 text-xs text-blue-400 hover:text-blue-300 tracking-wide"
          >
            See all contact options →
          </Link>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto mt-10 pt-6 border-t border-white/5 flex flex-wrap gap-3 justify-between text-xs text-zinc-600">
        <div>© {new Date().getFullYear()} Career GPS AI · AI-powered career tools</div>
        <div className="flex gap-4 sm:gap-6">
          <Link to="/contact" data-testid="footer-bottom-contact">Contact</Link>
          <a href="#">Terms</a>
          <Link
  to="/privacy-policy"
  data-testid="footer-link-privacy"
>
  Privacy
</Link>
        </div>
      </div>
    </footer>
  );
}
